import { zipSync, strToU8 } from 'fflate';
import { jsPDF } from 'jspdf';
import { toJpeg } from 'html-to-image';
import { formatLifecycleForDocument, type DiagramDocument } from '@ukrop-nm/shared';
import { exportDrawioXml } from '@ukrop-nm/drawio-importer';
import type { Node } from '@xyflow/react';
import { buildNetworkProjectHtml } from './buildNetworkProjectDoc';
import { captureDiagramJpeg, captureDiagramSvg, downloadDataUrl } from './captureDiagramJpeg';
import { composeDrawingSheet } from './composeDrawingSheet';
import { dataUrlToBytes, downloadBlob } from './bytes';
import type { DrawingSheetMeta } from './drawingMeta';
import { formatDrawingDateUk } from './drawingMeta';
import { TEXT_PAGE_LAYOUT_MM } from './textPageLayout';
import { equipmentCsv, linksCsv } from '../state/csvRegistries';

export function projectFileBase(doc: DiagramDocument): string {
  return `UKROP-NM-${doc.siteId}-project`.replace(/[^\w.-]+/g, '_');
}

async function waitForImages(el: HTMLElement) {
  const imgs = [...el.querySelectorAll('img')];
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              img.addEventListener('load', () => resolve(), { once: true });
              img.addEventListener('error', () => resolve(), { once: true });
            }),
    ),
  );
}

async function htmlToJpeg(el: HTMLElement, pixelRatio = 2): Promise<string> {
  document.body.appendChild(el);
  el.style.position = 'fixed';
  el.style.left = '-10000px';
  el.style.top = '0';
  el.style.zIndex = '-1';
  try {
    await document.fonts?.ready;
    await waitForImages(el);
    const sheet =
      (el.querySelector('.ukrop-sheet, .ukrop-proj') as HTMLElement | null) ?? el;
    return await toJpeg(sheet, {
      backgroundColor: '#ffffff',
      cacheBust: true,
      pixelRatio,
      quality: 0.95,
    });
  } finally {
    el.remove();
  }
}

function paintTextPageVerticalMargins(pdf: jsPDF) {
  const { width, height, top, bottom } = TEXT_PAGE_LAYOUT_MM;
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, width, top, 'F');
  pdf.rect(0, height - bottom, width, bottom, 'F');
}

/** Slice a long A4-width text raster onto portrait pages with 20 mm top/bottom margins. */
function addTextImagePages(pdf: jsPDF, imageDataUrl: string) {
  const pageW = TEXT_PAGE_LAYOUT_MM.width;
  const pageH = TEXT_PAGE_LAYOUT_MM.height;
  const contentH = pageH - TEXT_PAGE_LAYOUT_MM.top - TEXT_PAGE_LAYOUT_MM.bottom;
  const props = pdf.getImageProperties(imageDataUrl);
  const imgH = (props.height * pageW) / props.width;
  let heightLeft = imgH;
  let y = TEXT_PAGE_LAYOUT_MM.top;
  pdf.addImage(imageDataUrl, 'JPEG', 0, y, pageW, imgH, undefined, 'FAST');
  paintTextPageVerticalMargins(pdf);
  heightLeft -= contentH;
  while (heightLeft > 0) {
    y -= contentH;
    pdf.addPage('a4', 'portrait');
    pdf.addImage(imageDataUrl, 'JPEG', 0, y, pageW, imgH, undefined, 'FAST');
    paintTextPageVerticalMargins(pdf);
    heightLeft -= contentH;
  }
}

function diagramJson(doc: DiagramDocument): string {
  return JSON.stringify(doc, null, 2);
}

function projectReadme(doc: DiagramDocument, meta: DrawingSheetMeta, hasJpeg: boolean, hasSvg: boolean): string {
  const lifecycle = formatLifecycleForDocument(doc.lifecycle);
  const jpegLine = hasJpeg
    ? '- topology-drawing.jpg — креслення топології (рамка + основний напис)\n'
    : '- topology-drawing.jpg — немає (порожня схема або збій знімка)\n';
  const svgLine = hasSvg
    ? '- topology.svg — топологія SVG (світла тема, без рамки)\n'
    : '- topology.svg — немає (порожня схема або збій знімка)\n';
  return [
    'UKROP Network Monitor — пакет проєкту мережі',
    `Обʼєкт: ${meta.objectName || doc.name || doc.siteId}`,
    `Проєкт: ${meta.projectName}`,
    `Позначення: ${meta.sheetCode}`,
    `Ідентифікатор: ${doc.siteId}`,
    `Стадія проєкту: ${lifecycle.label}`,
    `Опис стадії: ${lifecycle.description}`,
    `Дата: ${formatDrawingDateUk(meta.date)}`,
    `Розробив: ${meta.author}`,
    '',
    'Файли:',
    jpegLine.trimEnd(),
    svgLine.trimEnd(),
    '- topology.drawio — схема для diagrams.net (нестиснений mxfile)',
    '- equipment.csv — реєстр обладнання (Excel, роздільник ;)',
    '- links.csv — реєстр ліній (Excel, роздільник ;)',
    '- technical-description.pdf — технічний опис (аркуш 1 = креслення, далі реєстри)',
    '- diagram.json — схема для повторного імпорту в редактор',
    '',
    'Креслення: світла тема, рамка та основний напис за принципами ISO 7200.',
    'Документ підготовлено за принципами TIA-606-C, ISO/IEC 14763-2 та ISO/IEC/IEEE 42010.',
    'Це не сертифікація відповідності зазначеним стандартам.',
    '',
  ].join('\n');
}

/** Raw light topology → framed A3 drawing sheet JPEG. */
export async function buildFramedDrawingJpeg(
  doc: DiagramDocument,
  nodes: Node[],
  meta: DrawingSheetMeta,
): Promise<string | null> {
  const topology = await captureDiagramJpeg(nodes);
  if (!topology) return null;
  const sheet = composeDrawingSheet(topology, meta, doc.siteId, doc.lifecycle);
  return htmlToJpeg(sheet, 2);
}

/**
 * PDF: landscape high-res drawing page, then portrait text registries
 * (topology is not re-shrunk inside a narrow HTML column).
 */
async function renderPdf(
  doc: DiagramDocument,
  framedDrawing: string | null,
  meta: DrawingSheetMeta,
): Promise<jsPDF> {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
  const landW = 297;
  const landH = 210;

  if (framedDrawing) {
    const props = pdf.getImageProperties(framedDrawing);
    const scale = Math.min(landW / props.width, landH / props.height);
    const w = props.width * scale;
    const h = props.height * scale;
    const x = (landW - w) / 2;
    const y = (landH - h) / 2;
    pdf.addImage(framedDrawing, 'JPEG', x, y, w, h, undefined, 'MEDIUM');
  } else {
    pdf.setFontSize(12);
    pdf.text('Креслення топології не згенеровано (порожня схема).', 14, 20);
  }

  const textSheet = buildNetworkProjectHtml(doc, null, meta, { includeTopology: false });
  const textJpeg = await htmlToJpeg(textSheet, 2);
  pdf.addPage('a4', 'portrait');
  addTextImagePages(pdf, textJpeg);
  return pdf;
}

export async function exportTopologyJpeg(
  doc: DiagramDocument,
  nodes: Node[],
  meta: DrawingSheetMeta,
): Promise<void> {
  const framed = await buildFramedDrawingJpeg(doc, nodes, meta);
  if (!framed) {
    throw new Error('На схемі немає блоків для знімка.');
  }
  downloadDataUrl(framed, `${projectFileBase(doc)}-topology-drawing.jpg`);
}

export async function exportProjectPdf(
  doc: DiagramDocument,
  nodes: Node[],
  meta: DrawingSheetMeta,
): Promise<void> {
  const framed = await buildFramedDrawingJpeg(doc, nodes, meta);
  const pdf = await renderPdf(doc, framed, meta);
  downloadBlob(pdf.output('blob'), `${projectFileBase(doc)}.pdf`);
}

export function exportDiagramJson(doc: DiagramDocument): void {
  downloadBlob(
    new Blob([diagramJson(doc)], { type: 'application/json' }),
    `${projectFileBase(doc)}.json`,
  );
}

export async function exportTopologySvg(doc: DiagramDocument, nodes: Node[]): Promise<void> {
  const svg = await captureDiagramSvg(nodes);
  if (!svg) {
    throw new Error('На схемі немає блоків для знімка.');
  }
  downloadDataUrl(svg, `${projectFileBase(doc)}-topology.svg`);
}

export function exportDrawio(doc: DiagramDocument): void {
  downloadBlob(
    new Blob([exportDrawioXml(doc)], { type: 'application/xml;charset=utf-8' }),
    `${projectFileBase(doc)}.drawio`,
  );
}

export function exportCsvZip(doc: DiagramDocument): void {
  const base = projectFileBase(doc);
  const zipped = zipSync({
    'equipment.csv': strToU8(equipmentCsv(doc)),
    'links.csv': strToU8(linksCsv(doc)),
  });
  downloadBlob(new Blob([zipped], { type: 'application/zip' }), `${base}-registries.zip`);
}

/** ZIP: framed drawing + PDF + JSON + SVG + draw.io + CSV. */
export async function exportProjectZip(
  doc: DiagramDocument,
  nodes: Node[],
  meta: DrawingSheetMeta,
): Promise<void> {
  const base = projectFileBase(doc);
  const framed = await buildFramedDrawingJpeg(doc, nodes, meta);
  const svg = await captureDiagramSvg(nodes);
  const pdf = await renderPdf(doc, framed, meta);
  const pdfBytes = new Uint8Array(pdf.output('arraybuffer'));
  const files: Record<string, Uint8Array> = {
    [`${base}/technical-description.pdf`]: pdfBytes,
    [`${base}/diagram.json`]: strToU8(diagramJson(doc)),
    [`${base}/topology.drawio`]: strToU8(exportDrawioXml(doc)),
    [`${base}/equipment.csv`]: strToU8(equipmentCsv(doc)),
    [`${base}/links.csv`]: strToU8(linksCsv(doc)),
    [`${base}/README_UA.txt`]: strToU8(projectReadme(doc, meta, Boolean(framed), Boolean(svg))),
  };
  if (framed) {
    files[`${base}/topology-drawing.jpg`] = dataUrlToBytes(framed);
  }
  if (svg) {
    files[`${base}/topology.svg`] = dataUrlToBytes(svg);
  }
  const zipped = zipSync(files);
  downloadBlob(new Blob([zipped], { type: 'application/zip' }), `${base}.zip`);
}
